import React, { useEffect, useState } from 'react';

import AttorneyCard from './components/AttorneyCard';
import Button from '../../components/ui/Button';
import Footer from '../homepage/components/Footer';
import Header from '../../components/ui/Header';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';
import { attorneysService } from '../../services/attorneysService';

const AttorneysPage = () => {
	const [attorneys, setAttorneys] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		loadAttorneys();
		document.title = 'Our Attorneys - Fepa Law Partners | Expert Legal Team';
	}, []);

	const loadAttorneys = async () => {
		try {
			setLoading(true);
			const { data, error } = await attorneysService.getAllAttorneys();
			if (error) throw error;
			setAttorneys(data || []);
		} catch (error) {
			console.error('Error loading attorneys:', error);
			setError('Failed to load attorneys');
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>Our Attorneys - Fepa Law Partners | Expert Legal Team</title>
				<meta
					name='description'
					content="Meet Fepa Law Partners' team of experienced attorneys. Our lawyers specialize in commercial law, family law, civil litigation, criminal defense, real estate, and immigration law. Schedule a consultation today."
				/>
				<meta
					name='keywords'
					content='attorneys, lawyers, legal team, commercial lawyer, family law attorney, civil litigation lawyer, criminal defense lawyer, real estate attorney, immigration lawyer, Port Harcourt'
				/>
			</Helmet>

			<div className='min-h-screen bg-gray-50'>
				<Header />
				{/* Hero Section */}
				<section className='relative bg-gradient-to-r from-[#800020] to-[#800020]/80 pt-20 pb-16'>
					<div className='absolute inset-0 bg-black/20'></div>
					<div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						<div className='text-center'>
							<div className='inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6'>
								<Icon name='Users' size={20} color='white' />
								<span className='text-white font-source font-medium'>Meet Our Legal Team</span>
							</div>
							<h1 className='font-playfair font-bold text-4xl lg:text-6xl text-white mb-6'>
								Expert Attorneys
								<span className='block text-[#DB954E]'>Dedicated to Your Success</span>
							</h1>
							<p className='text-xl text-white/90 max-w-3xl mx-auto leading-relaxed'>
								Our distinguished team of legal professionals brings decades of combined experience
								across multiple practice areas, ensuring exceptional representation for every
								client.
							</p>
						</div>
					</div>
				</section>
				{/* Main Content */}
				<section className='py-16'>
					<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						{loading ? (
							<div className='flex items-center justify-center py-16'>
								<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
								<span className='ml-2 text-gray-600'>Loading attorneys...</span>
							</div>
						) : error ? (
							<div className='text-center py-16'>
								<Icon name='AlertCircle' className='h-12 w-12 mx-auto mb-4 text-red-500' />
								<p className='text-red-600 mb-4'>{error}</p>
								<Button
									onClick={loadAttorneys}
									className='bg-[#800020] hover:bg-[#800020]/90 text-white px-6 py-2 rounded-lg'
								>
									Try Again
								</Button>
							</div>
						) : attorneys.length === 0 ? (
							<div className='text-center py-16'>
								<Icon name='Users' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
								<p className='text-gray-500'>No attorneys found</p>
							</div>
						) : (
							/* Attorneys Grid */
							<div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8'>
								{attorneys?.map((attorney) => (
									<AttorneyCard key={attorney?.id} attorney={attorney} />
								))}
							</div>
						)}
					</div>
				</section>
				{/* CTA Section */}
				<section className='bg-gradient-to-r from-[#800020]/5 to-[#DB954E]/5 py-16'>
					<div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
						<h2 className='font-playfair font-bold text-3xl lg:text-4xl text-[#800020] mb-6'>
							Ready to Get Started?
						</h2>
						<p className='text-lg text-gray-600 mb-8 max-w-2xl mx-auto'>
							Schedule a consultation with one of our expert attorneys today. We're here to provide
							the legal guidance you need.
						</p>
						<div className='flex flex-col sm:flex-row gap-4 justify-center'>
							<Link to='/contact'>
								<Button
									variant='default'
									size='lg'
									className='bg-[#800020] hover:bg-[#800020]/90 text-white px-8 py-3 rounded-lg font-source font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2'
								>
									<Icon name='Calendar' size={20} />
									<span>Schedule a Consultation</span>
								</Button>
							</Link>
							<Button
								variant='outline'
								size='lg'
								className='border-2 border-[#800020] text-[#800020] hover:bg-[#800020] hover:text-white px-8 py-3 rounded-lg font-source font-semibold transition-all duration-300 flex items-center justify-center space-x-2'
							>
								<Icon name='Phone' size={20} />
								<span>Call (+234) 909-552-9557</span>
							</Button>
						</div>
					</div>
				</section>
				<Footer />
			</div>
		</>
	);
};

export default AttorneysPage;
